// src/config/version.js
// =============================================================================
// Red Setter Homes Portal - Version Configuration
// =============================================================================
//
// VERSION SYSTEM:
//   Major.Minor.Patch (e.g., v1.0.47)
//   - Major & Minor: Manually updated for milestones/features
//   - Patch: Auto-calculated from git commit count at build time
//
// HOW TO BUMP VERSIONS:
//   1. Update the major or minor number below
//   2. Set the corresponding baseline to the CURRENT total commit count
//      for that portal's files (run the git command shown below)
//   3. Patch will reset to 0 automatically
//
// TO GET CURRENT COMMIT COUNTS (run in project root):
//   Admin:  git rev-list --count HEAD -- src/pages/admin/ src/components/admin/
//   Client: git rev-list --count HEAD -- src/pages/client/ src/components/Navbar.js src/components/Navbar.css
//
// =============================================================================

/**
 * @fileoverview Version configuration for Red Setter Homes Portal.
 * Manages semantic versioning with auto-incrementing patch numbers
 * calculated from git commit counts at build time.
 * @module config/version
 */

/**
 * Admin portal version configuration.
 * Update major/minor for intentional milestones.
 * @type {{ major: number, minor: number, baseline: number }}
 */
const ADMIN_VERSION = {
  major: 0,
  minor: 1,
  /** Commit count at time of last major/minor bump (resets patch to 0) */
  baseline: 0,
};

/**
 * Client portal version configuration.
 * Update major/minor for intentional milestones.
 * @type {{ major: number, minor: number, baseline: number }}
 */
const CLIENT_VERSION = {
  major: 0,
  minor: 1,
  /** Commit count at time of last major/minor bump (resets patch to 0) */
  baseline: 0,
};

/**
 * Computes the full semantic version string for a given portal.
 * Patch number = (build-time commit count) - baseline.
 *
 * @param {'admin' | 'client'} portal - Which portal to get the version for
 * @returns {string} Formatted version string, e.g. "v1.0.47"
 */
export function getPortalVersion(portal) {
  const config = portal === "admin" ? ADMIN_VERSION : CLIENT_VERSION;
  const envVar =
    portal === "admin"
      ? process.env.REACT_APP_ADMIN_COMMITS
      : process.env.REACT_APP_CLIENT_COMMITS;

  const commitCount = parseInt(envVar, 10) || 0;
  const patch = Math.max(0, commitCount - config.baseline);

  return `v${config.major}.${config.minor}.${patch}`;
}

/**
 * Gets the admin portal version string.
 * @returns {string} e.g. "v1.0.47"
 */
export function getAdminVersion() {
  return getPortalVersion("admin");
}

/**
 * Gets the client portal version string.
 * @returns {string} e.g. "v1.0.23"
 */
export function getClientVersion() {
  return getPortalVersion("client");
}
